/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */

// deps
import React, { memo, useCallback, useContext, useRef, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
// internal-deps
import {
    ElementObserver,
    isEmpty,
    useEffectOnce,
    clearTimerFromStorage,
    isNetworkError,
    LocalStorage,
    LOCAL_STORAGE_KEYS,
    scrollElementIntoViewWithOffset,
    Source,
    RouteProps,
} from "@swiggy-private/core-ui";
import { EditNumberWidget, CART_STATUS_CODES, GenericCartToast, GiftingCouponModal } from "@swiggy-private/cart-ui";
import { CancellationPolicyRef, CouponConfetti } from "@swiggy-private/cart-types";
import Blaze from "@swiggy-private/blaze.js";
import { NavigationScreenLinkTypes, NavigationTypes } from "@swiggy-private/native-bridge";
import {
    useNavigate,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    AnalyticsContext,
    CheckoutContext,
    CheckoutActionTypes,
    CheckoutRoute,
    getBridgeSdk,
    UserContext,
    recordNREvent,
    getUserFromUserContextValue,
    VisualEffectContext,
    NEWRELIC_TABLES,
    BLAZE_EVENTS,
} from "@swiggy-private/checkout-core";
//styles
import styles from "../../cart-renderer/CartRenderer.scss";
import giftingCustomStyles from "./GiftingCartRenderer.scss";
// components
import { CartFooter } from "@cart-client/components/cart-footer";
import { UpdateProgressLoader } from "@swiggy-private/core-ui";
import CartUIWrapper from "../../cart-renderer/cart-ui-wrapper";
import { CartTimerWrapper } from "@cart-client/components/cart-timer-wrapper";
import { Shimmer } from "@cart-client/components/shimmer";
import { CartWidgetRenderer } from "@cart-client/components/cart-widget-renderer";
import { AddressListManager } from "@cart-client/components/address-list-manager";
import GenericCroutonWrapper from "@cart-client/components/generic-crouton-wrapper/GenericCroutonWrapper";
import { ReviewItemListWrapper } from "@cart-client/components/review-item-list-wrapper/ReviewItemListWrapper";
import {
    GiftingCartMultiDeliveryInfo,
    NetworkConnectivityError,
    TooManyRequestsError,
    SessionExpiredError,
    EmptyGiftingCartError,
    DefaultGiftingCartError,
} from "@swiggy-private/cart-ui";
// constants
import { SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";
import { FLOW_TYPE_OFO, HTTP_STATUS_CODES } from "@cart-client/constants/index";
import { DEFAULT_PAYMENTS_RESPONSE_KEY } from "@cart-client/constants/StorageKeys";
import { confettiMapping } from "@cart-client/constants/Coupon";
import {
    CART_ELEMENT_IDS,
    SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
    SCROLL_MARGIN_TOP_WITHOUT_BANNER,
} from "@cart-client/components/cart-renderer/constants";

// helpers
import {
    isGiftingCartEmpty,
    buildErrorData,
    getRestaurantNameForGiftingCart,
    getNumberOfDeliveriesForGiftingCart,
} from "@cart-client/helpers/GiftingCart";
import { recordGiftingErrorNREvent } from "@cart-client/components/gifting-cart/gifting-cart-renderer/utils";
import {
    getMenuItems,
    getCouponErrorCard,
    getRestaurantId,
    getTipAmount,
    getSavingsBannerData,
    getIsAwayLocation,
    getOrderFlowType,
    getIrctcIdentifier,
    getOFOWidgetDetails,
    extractOFOAddressFromEditAddressNB,
    getQFoodSLA,
    getCartId,
    handleAddMoreItemsClick,
} from "@cart-client/helpers/Cart";
import { isClosingTimeAvailable, setClosingTimeInStorage } from "@cart-client/helpers/TimerOnCart";
import { isTurboPaymentEnabled } from "@cart-client/helpers/TurboPayment";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import { syncCartAddressHeaders } from "@cart-client/helpers/Header";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { openEditNumberBottomSheet } from "@cart-client/helpers/OFO";
import { recordCartPercievedTime } from "@cart-client/helpers/Metric";
import { isCartTotalZero, isItemExceededLimitMessageExists, isOnTimeCart } from "@cart-client/helpers/CartItemRenderer";
import { goToLogin } from "@cart-client/helpers/Login";
import { getMenuCalloutInfo } from "@cart-client/helpers/Cart";
// hooks
import useFetchGiftingCart from "@cart-client/hooks/useFetchGiftingCart";
import { useCartToast } from "@cart-client/hooks/useCartToast";
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { usePrefetchTickScreenLottie } from "@cart-client/hooks/usePrefetchTickScreenLottie";
// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";
//analytics
import {
    clickProceedToPay,
    clickViewDetailedBill,
    clickAddMoreItemsCta,
    onWebcartUpdateImpression,
} from "@cart-client/analytics/events";
import {
    clickAddDetails,
    clickEditDetails,
    impressionAddDetails,
    impressionEditDetails,
} from "@cart-client/analytics/events/OFO";
import useAutoApplyCallout from "@cart-client/hooks/useAutoApplyCallout";
// interfaces
import { OFOWidgetPosition } from "@cart-client/interfaces/Cart";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import { useGiftingCouponAnalytics } from "@cart-client/components/coupon-listing/hooks/useGiftingCouponAnalytics";
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { logCartViewedEvent } from "@cart-client/helpers/GiftingClevertap";

export enum STICKY_BANNER {
    OFO = "OFO_Widget",
    SAVING = "Saving_Banner",
}

// --- HARD CONFIGS ---
export const VoidFunction = () => {
    // no-op
};
const isGroupCartFlow = false;
const isValueMeal = false;
const isBulkOrderFn = false;

function GiftingCartRenderer(_props: RouteProps<CheckoutRoute>): React.ReactElement {
    const [intialCartLoaded, setInitalCartLoaded] = useState(false);
    const { loading, localCartState, reviewedCartState, error: cartApiError } = useFetchGiftingCart();
    const { cartState, setCartState } = useContext(CartContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { showConfetti } = useContext(VisualEffectContext);
    const { user: userContextValue } = useContext(UserContext);
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);
    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);
    const { reviewedCart: cartResponse, localCart } = cartState;
    const { cartFeedbackXP } = useCartRendererXP(cartResponse);
    const [forceExtraPaddingOnCart, setForceExtraPaddingOnCart] = useState(false);
    const { setUser } = useContext(UserContext);

    const { onTimerLoad } = useCartRendererAnalytics(cartResponse);
    const giftingAnalytics = useGiftingCouponAnalytics({
        restaurantId: cartResponse?.data?.restaurant_details?.id,
        originalCartId: (cartResponse as GiftingReviewedCart)?.data?.originalCartId,
        cartId: (cartResponse as GiftingReviewedCart)?.data?.cartId,
    });
    usePrefetchTickScreenLottie();
    const [isCroutonVisible, setIsCroutonVisible] = useState(false);
    const setCroutonVisibility = useCallback((croutonVisibility: boolean) => {
        setIsCroutonVisible(croutonVisibility);
    }, []);
    useEffect(() => {
        setIsTurboPayWidgetHidden(false);
    }, [cartState.uniqueIdForCart]);
    const hideTurboPayWidget = useCallback(() => {
        setIsTurboPayWidgetHidden(true);
    }, []);
    const { updateCartInProgress, updateCartError, doUpdateCart, reloadCart, showCouponModal, toggleCouponModal } =
        useContext(UpdateCartContext);
    const showCartToast = useCartToast(cartResponse, isCroutonVisible);
    const { cartToastData } = useContext(CartToastContext);
    const { couponSuccessData } = useAutoApplyCallout(cartResponse, toggleCouponModal);
    const isCartStatusSuccess = useMemo(
        () => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS,
        [cartResponse?.statusCode],
    );
    const { displayAddressList, goToSelectAddress, selectedAddress } = useFetchAddressOnClick(
        cartResponse?.data?.cart_total,
        cartResponse?.data?.addresses,
        isCartStatusSuccess,
    );
    const qFoodSLA = useMemo(() => getQFoodSLA(cartResponse), [cartResponse]);
    const userLoggedIn = isUserLoggedIn();
    const cancellationPolicyRef = useRef<CancellationPolicyRef>();
    const makePaymentButtonRef = useRef<HTMLDivElement>(null);
    const planAddedRef = useRef<HTMLDivElement>(null);
    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";
    const sessionId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
    const showCouponConfetti = LocalStorage.getItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN) !== sessionId;
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const disableRedirectionToMenu = isValueMeal;

    const canShowTimer = cartResponse?.statusCode === 0 && isClosingTimeAvailable(cartResponse);
    const savingsBannerData = getSavingsBannerData(cartResponse);
    const canShowFooterCrouton = useMemo(
        () => !canShowTimer && !isItemExceededLimitMessageExists(cartResponse),
        [canShowTimer, cartResponse],
    );
    const showOFOWidget = useMemo(
        () => getOrderFlowType(cartResponse) === FLOW_TYPE_OFO || getIsAwayLocation(cartResponse),
        [cartResponse],
    );
    const [isPageScrolled, setPageScrolled] = useState(false);
    const deliverySlots = cartResponse?.data?.slot_details;
    useEffect(() => {
        // set closing time in storage
        setClosingTimeInStorage(cartResponse);
    }, [cartResponse]);

    const errorData = useMemo(() => buildErrorData(cartResponse), [cartResponse]);

    const onTimerEnd = useCallback(async () => {
        clearTimerFromStorage();
        reloadCart();
        recordNREvent({ eventType: NEWRELIC_TABLES.TIMER_END_UPDATE_CART_CALLED });
    }, [reloadCart]);
    const navigateTo = useNavigate();

    const deliveryCountsForCart = useMemo(
        () => getNumberOfDeliveriesForGiftingCart(cartResponse as GiftingReviewedCart),
        [cartResponse],
    );

    // If cart total is 0, we will direclty place the order from cart itself
    const canPlaceOrderFromCart = useMemo(() => isCartTotalZero(cartResponse), [cartResponse]);
    const turboPaymentEnabled = useMemo(() => {
        return isTurboPaymentEnabled(cartResponse, isTurboPayWidgetHidden);
    }, [cartResponse, isTurboPayWidgetHidden]);
    const gotoPayment = useCallback(() => {
        // log payment start intreaction time, any change here should be reflected in `packages/payment-client/src/components/payment-main/PaymentMain.tsx` as well
        Blaze.recordStart(BLAZE_EVENTS.RECORD_CHECKOUT_PAYMENT_LOAD_TIME);

        // clear cached payment data before goiing to payment screen from web cart
        // We also clear cache data if going to payment page from `Change` cta on turbo widget
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromGiftingCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({ show: false });
        }

        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
            payload: {},
        });
        navigateTo(CheckoutRoute.PAYMENTS, {}, false);
    }, [isFromBottomBar, isSessionStorageAvailable, navigateTo, setCheckoutState]);

    const placeOrderFromCart = useCallback(() => {
        // clear cached payment data before goiing to payment screen from web cart
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromGiftingCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({ show: false });
        }
        const cartTotal = cartResponse?.data?.cart_total;
        setCheckoutState({
            type: CheckoutActionTypes.DIRECT_PLACE_ORDER,
            payload: { fromCart: true, cartTotal: cartTotal || 0, orderComment: localCart?.orderComment || "" },
        });
    }, [isSessionStorageAvailable, isFromBottomBar, setCheckoutState, cartResponse, localCart]);

    const viewDetailedBill = useCallback(() => {
        clickViewDetailedBill(cartResponse?.data?.cart_total ?? 0, clickEvent);
        scrollElementIntoViewWithOffset(
            CART_ELEMENT_IDS.BILL_DETAILS_CONTAINER,
            isEmpty(savingsBannerData) ? SCROLL_MARGIN_TOP_WITHOUT_BANNER : SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
            true,
            CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_SELECTOR,
        );
    }, [cartResponse, clickEvent, savingsBannerData]);

    // Footer action can be either go to payment screen or directly place order from cart in case of 0 amount to pay
    const cartFooterAction = useMemo(
        () => (canPlaceOrderFromCart ? placeOrderFromCart : gotoPayment),
        [canPlaceOrderFromCart, gotoPayment, placeOrderFromCart],
    );

    const onMakePayment = useCallback(() => {
        clickProceedToPay(clickEvent, {
            cartAmount: cartResponse?.data?.cart_total ?? 0,
            restaurantId: cartResponse?.data?.restaurant_details?.id,
        });
        const cartId = cartResponse?.data?.cartId || "";
        const addressId = cartResponse?.data?.address_id || "";
        syncCartAddressHeaders({ cartId, addressId });
        // show cancellation popup if required
        if (cancellationPolicyRef && cancellationPolicyRef.current) {
            cancellationPolicyRef.current.interceptPayment();
        } else {
            cartFooterAction();
        }
    }, [
        cartResponse?.data?.cartId,
        cartResponse?.data?.address_id,
        cartResponse?.data?.cart_total,
        cartResponse?.data?.restaurant_details?.id,
        clickEvent,
        cartFooterAction,
    ]);

    useEffect(() => {
        if (!loading && reviewedCartState != null && intialCartLoaded === false) {
            const payload = {
                uniqueIdForCart: randomStringIdGenerator(),
                localCart: localCartState,
                reviewedCart: reviewedCartState,
            };

            // log clevertap event for cart viewed
            logCartViewedEvent(reviewedCartState);

            recordCartPercievedTime();
            // update cart state
            setCartState({ type: CartActionTypes.UPDATE_CART, payload });
            setInitalCartLoaded(true);
        }
    }, [localCartState, reviewedCartState, loading, setCartState, user, intialCartLoaded]);

    useEffectOnce(() => {
        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({
                show: true,
            });
        }
    });

    const goToHome = (): void => {
        getBridgeSdk().navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.HOME,
        });
    };

    const goToMenu = useCallback(() => {
        const cartId = String(cartResponse?.data?.cartId ?? "");
        clickAddMoreItemsCta(clickEvent, cartId);
        updateStatusBarCleanup();
        handleAddMoreItemsClick({ isGroupCartFlow, isFromBottomBar, cartResponse });
    }, [cartResponse, clickEvent, isFromBottomBar]);

    const showAddressWidgetTop = useMemo(() => userLoggedIn && selectedAddress, [userLoggedIn, selectedAddress]);

    const triggerCouponConfetti = useCallback(
        (type: CouponConfetti) => {
            switch (type) {
                case CouponConfetti.TOP_UP:
                    showConfetti(confettiMapping.CONFETTI_TYPE_TOP_UP);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.AUTO_APPLY:
                    showConfetti(confettiMapping.CONFETTI_TYPE_AUTO_APPLY);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.INVALID:
                default:
                    break;
            }
        },
        [showConfetti, sessionId],
    );

    const { titleOFOWidget, subTitleOFOWidget, actionTextOFOWidget, showGiftIcon, widgetPosition } = useMemo(
        () => getOFOWidgetDetails(selectedAddress, getOrderFlowType(cartResponse), getIsAwayLocation(cartResponse)),
        [selectedAddress, cartResponse],
    );

    const canShowOFOWidgetAtTop = useMemo(
        () => showOFOWidget && !!titleOFOWidget && widgetPosition !== OFOWidgetPosition.BOTTOM,
        [showOFOWidget, titleOFOWidget, widgetPosition],
    );

    const onClickOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            clickEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, clickEvent);
        } else {
            clickAddDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, false, clickEvent);
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, clickEvent]);

    const onImpressionOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            impressionEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, impressionEvent);
        } else {
            impressionAddDetails(
                cartResponse?.data?.cartId || "",
                !!localCartState?.userLiveLat,
                false,
                impressionEvent,
            );
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, impressionEvent]);

    const onOFOWidgetActionClick = useCallback(() => {
        onClickOFOWidgetEvent();
        selectedAddress &&
            openEditNumberBottomSheet(selectedAddress, actionTextOFOWidget).then((response) => {
                const parsedAddress = extractOFOAddressFromEditAddressNB(response);
                const newAddressId = parsedAddress?.id || localCart?.address_id;
                doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: newAddressId?.toString(),
                });
            });
    }, [actionTextOFOWidget, doUpdateCart, localCart, selectedAddress, onClickOFOWidgetEvent]);

    const setScrolledState = useCallback(() => {
        setPageScrolled(true);
    }, []);

    const unsetScrolledState = useCallback(() => {
        setPageScrolled(false);
    }, []);

    const { isAvailable, info } = useMemo(() => {
        return getMenuCalloutInfo(cartResponse);
    }, [cartResponse]);

    /**
     * This hack of passing component as a prop to billdetail is done because
     * product wants to reposition and play around widgets on priority.
     * This would be fixed in PL migration
     */

    const EditNumberWidgetOfo = useMemo(() => {
        return (
            <EditNumberWidget
                title={titleOFOWidget}
                subTitle={subTitleOFOWidget}
                actionText={actionTextOFOWidget}
                showGiftIcon={showGiftIcon}
                onActionClick={onOFOWidgetActionClick}
                onImpression={onImpressionOFOWidgetEvent}
            />
        );
    }, [
        titleOFOWidget,
        subTitleOFOWidget,
        actionTextOFOWidget,
        showGiftIcon,
        onOFOWidgetActionClick,
        onImpressionOFOWidgetEvent,
    ]);

    const renderOfoWidgetAtTop = useMemo(() => {
        if (canShowOFOWidgetAtTop) {
            return (
                <div
                    className={classNames(styles.containerOfoDetailedBanner, {
                        [styles.containerOfoDetailedBanner]: subTitleOFOWidget,
                        [styles.containerOfoBanner]: !subTitleOFOWidget,
                    })}
                >
                    {EditNumberWidgetOfo}
                </div>
            );
        }
        return null;
    }, [canShowOFOWidgetAtTop, subTitleOFOWidget, EditNumberWidgetOfo]);

    const show429ErrorScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            // this is for when we update cart after load and it fails
            (!loading &&
                !isEmpty(cartApiError) &&
                cartApiError.httpStatusCode == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) ||
            (!updateCartInProgress && updateCartError?.httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS)
        );
    }, [cartApiError, loading, updateCartError?.httpStatusCode, updateCartInProgress]);

    const showNetworkConnectivityScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            !loading && cartApiError?.errorPayload && isNetworkError(cartApiError?.errorPayload)
        );
    }, [cartApiError, loading]);

    useEffect(() => {
        if (checkoutState.type !== CheckoutActionTypes.SHOW_GIFTING_CART) {
            return;
        }

        if (turboPaymentEnabled) {
            const cartId = cartResponse?.data?.cartId || "";
            const addressId = cartResponse?.data?.address_id || "";

            syncCartAddressHeaders({ cartId, addressId });
        }
    }, [cartResponse?.data?.address_id, cartResponse?.data?.cartId, checkoutState, turboPaymentEnabled]);

    useEffect(() => {
        if (cartResponse && cartResponse?.data) {
            const cartId = cartResponse?.data?.cartId || "";
            onWebcartUpdateImpression(impressionEvent, {
                cartId,
                restaurantId: getRestaurantId(cartResponse),
                cartTotal: cartResponse?.data.cart_total,
                couponCode: cartResponse?.data?.coupon_code,
                couponApplied: !!cartState.localCart?.isCouponValid,
                turboPaymentEnabled,
                xpValues: JSON.stringify(cartResponse?.data.XPData),
            });
        }
    }, [cartResponse, cartResponse?.data, cartState.localCart?.isCouponValid, impressionEvent, turboPaymentEnabled]);

    const couponErrorData = getCouponErrorCard(cartResponse?.data);
    const renderCouponModal = useMemo(() => {
        return showCouponModal && (!isEmpty(couponSuccessData) || !isEmpty(couponErrorData));
    }, [showCouponModal, couponSuccessData, couponErrorData]);

    const coreLayoutCustomClassName = classNames({
        [giftingCustomStyles.giftingCoreLayoutContainer]: true,
    });

    const loginHandlerCallback = useCallback(() => {
        goToLogin("GIFTING_CART", reloadCart, setUser);
    }, [reloadCart, setUser]);

    if (showNetworkConnectivityScreen) {
        return (
            <CartUIWrapper
                disableRedirectionToMenu={disableRedirectionToMenu}
                headerClass={styles.headerError}
                coreLayoutCustomClassName={coreLayoutCustomClassName}
            >
                <NetworkConnectivityError nrEventHandler={recordGiftingErrorNREvent} />
            </CartUIWrapper>
        );
    }

    if (show429ErrorScreen) {
        return (
            <CartUIWrapper
                disableRedirectionToMenu={disableRedirectionToMenu}
                headerClass={styles.headerError}
                coreLayoutCustomClassName={coreLayoutCustomClassName}
            >
                <TooManyRequestsError nrEventHandler={recordGiftingErrorNREvent} />
            </CartUIWrapper>
        );
    }

    if (!cartResponse || loading) {
        return (
            <CartUIWrapper
                disableRedirectionToMenu={disableRedirectionToMenu}
                coreLayoutCustomClassName={coreLayoutCustomClassName}
            >
                <Shimmer />
            </CartUIWrapper>
        );
    }

    const isSessionExpiredError = cartResponse.statusCode === CART_STATUS_CODES.SESSION_EXPIRED;
    if (!cartResponse.data || cartResponse.statusCode === CART_STATUS_CODES.FAILURE || isSessionExpiredError) {
        return (
            <CartUIWrapper
                disableRedirectionToMenu={disableRedirectionToMenu}
                headerClass={styles.headerError}
                coreLayoutCustomClassName={coreLayoutCustomClassName}
            >
                {(() => {
                    if (isSessionExpiredError) {
                        return (
                            <SessionExpiredError
                                loginHandler={loginHandlerCallback}
                                nrEventHandler={recordGiftingErrorNREvent}
                            />
                        );
                    }
                    return <DefaultGiftingCartError nrEventHandler={recordGiftingErrorNREvent} />;
                })()}
            </CartUIWrapper>
        );
    }

    if (isGiftingCartEmpty(cartResponse.data)) {
        return (
            <CartUIWrapper
                disableRedirectionToMenu={disableRedirectionToMenu}
                headerClass={styles.headerError}
                coreLayoutCustomClassName={coreLayoutCustomClassName}
            >
                <EmptyGiftingCartError nrEventHandler={recordGiftingErrorNREvent} />
            </CartUIWrapper>
        );
    }

    const shouldShowExtraPaddingUnservicableRx =
        !isCartStatusSuccess || !showAddressWidgetTop || selectedAddress?.delivery_valid !== 1;
    const showExtraPaddingBottom = shouldShowExtraPaddingUnservicableRx || forceExtraPaddingOnCart;

    const containerClassName = classNames(styles.bottomPadding, {
        [styles.topPadding]: isEmpty(savingsBannerData) && !canShowOFOWidgetAtTop && !showAddressWidgetTop,
        [styles.bottomPaddingExtra]: showExtraPaddingBottom,
    });

    const headerClassName = classNames({
        [styles.header]: isEmpty(savingsBannerData) && !canShowOFOWidgetAtTop,
        [styles.headerV2]: true,
        [styles.headerScrolled]: isPageScrolled,
        [styles.headerAddressWidgetTopScrolledSavings]: !isEmpty(savingsBannerData),
    });

    return (
        <CartUIWrapper
            disableRedirectionToMenu={disableRedirectionToMenu}
            containerId={CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_ID}
            containerClass={containerClassName}
            headerClass={headerClassName}
            coreLayoutCustomClassName={coreLayoutCustomClassName}
            showCartFeedback={cartFeedbackXP}
            statusCode={cartResponse.statusCode}
            restaurantName={getRestaurantNameForGiftingCart(cartResponse)}
            cartId={getCartId(cartResponse)}
            restaurantId={getRestaurantId(cartResponse)}
            isPageScrolled
            selectedAddress={selectedAddress}
            onAddressDropdownClick={goToSelectAddress}
            qFoodSLA={qFoodSLA}
            showAddressWidgetTop={!!showAddressWidgetTop}
            irctcData={getIrctcIdentifier(cartResponse)}
            showGreenSubtitle={isOnTimeCart(cartResponse?.data?.promises)}
            incognitoModeToggleHandler={VoidFunction}
            isIncognitoCart={false}
            isSavingsBannerExist={!isEmpty(savingsBannerData)}
            goToHome={goToHome}
            useHeaderV2={true}
        >
            <ElementObserver
                onViewEnter={unsetScrolledState}
                observerClassName={styles.observerNoWidth}
                onViewExit={setScrolledState}
            >
                <div className={styles.marginBottomDiv} />
            </ElementObserver>

            {renderOfoWidgetAtTop}
            {isAvailable && (
                <GiftingCartMultiDeliveryInfo multiDeliveryInfo={info} deliveryCount={deliveryCountsForCart} />
            )}
            <ReviewItemListWrapper />
            {renderCouponModal && (
                <GiftingCouponModal
                    couponSuccessData={couponSuccessData}
                    couponErrorData={couponErrorData}
                    closeModal={toggleCouponModal}
                    onImpressionGiftingModal={giftingAnalytics.handleGiftingModalImpression}
                    onClickCloseGiftingModal={giftingAnalytics.handleGiftingModalCloseClick}
                    onClickCTAGiftingModal={giftingAnalytics.handleGiftingModalCTAClick}
                    triggerConfettiByType={triggerCouponConfetti}
                    showCouponConfetti={showCouponConfetti}
                />
            )}
            <div className={styles.secondContainer}>
                <CartWidgetRenderer
                    canShowTimer={canShowTimer}
                    setCroutonVisibility={setCroutonVisibility}
                    canShowOFOWidgetAtTop={canShowOFOWidgetAtTop}
                    planAddedRef={planAddedRef}
                    goToMenu={goToMenu}
                    isCroutonVisible={isCroutonVisible}
                    isValueMeal={isValueMeal}
                />
            </div>
            {/* <ScreenReaderOnly as="button" text={ACCESSIBLITY_TEXTS.SKIP_TO_PAYMENT} onClick={focusMakePaymentButton} /> */}
            {canShowTimer && (
                <CartTimerWrapper
                    source={Source.CART}
                    onComplete={onTimerEnd}
                    onTimerLoad={onTimerLoad}
                    setCroutonVisibility={setCroutonVisibility}
                />
            )}
            {showCartToast && <GenericCartToast toastData={cartToastData} />}
            <CartFooter
                addressId={cartResponse.data.address_id}
                addresses={cartResponse.data.addresses}
                cartTotal={cartResponse.data.cart_total}
                tipAmount={getTipAmount(cartResponse)}
                crouton={cartResponse.data.crouton}
                canShowCrouton={canShowFooterCrouton}
                statusCode={cartResponse.statusCode}
                statusMessage={cartResponse.statusMessage}
                titleMessage={cartResponse.titleMessage}
                onMakePayment={onMakePayment}
                ctaText={cartResponse.ctaText}
                ctaAction={cartResponse.ctaAction}
                viewDetailedBill={viewDetailedBill}
                cartItems={getMenuItems(cartResponse)}
                canPlaceOrderFromCart={canPlaceOrderFromCart}
                makePaymentButtonRef={makePaymentButtonRef}
                qFoodSLA={qFoodSLA}
                deliverySlots={deliverySlots}
                restaurantId={cartResponse.data.restaurant_details?.id}
                cartId={cartResponse.data.cartId}
                isTurboPayTooltipHidden={isCroutonVisible}
                setCroutonVisibility={setCroutonVisibility}
                isTurboPaymentEnabled={turboPaymentEnabled}
                setIsTurboPayWidgetHidden={hideTurboPayWidget}
                updateCartInProgress={updateCartInProgress}
                isBulkOrder={isBulkOrderFn}
                irctcData={getIrctcIdentifier(cartResponse)}
                setForceExtraPaddingOnCart={setForceExtraPaddingOnCart}
                errorData={errorData}
            />
            <AddressListManager reviewedCart={cartResponse} displayAddressList={displayAddressList} />
            {updateCartInProgress && <UpdateProgressLoader />}
            <GenericCroutonWrapper />
        </CartUIWrapper>
    );
}

export default memo(GiftingCartRenderer);
